import { makeAutoObservable, runInAction } from 'mobx';
import type { RootStore, ServiceStore } from '@intrasync/stores';
import type {
    Product,
    ProductCategory,
    ProductVariant,
    BillOfMaterials,
    BOMItem,
    ProductPricing,
    ProductInventory,
    ProductData,
    ProductVariantData,
    BillOfMaterialsData,
    BOMItemData,
    ProductPricingData,
    ProductInventoryData
} from '../../types/Product';

class ProductStore {
    rootStore: RootStore;

    // Observables
    isLoading = false;
    error: string | null = null;

    // Product data
    products: Product[] = [];
    selectedProduct: Product | null = null;
    productsLoading = false;
    productsError: string | null = null;

    // Categories
    categories: ProductCategory[] = [];
    categoryTree: ProductCategory[] = [];
    categoriesLoading = false;
    categoriesError: string | null = null;

    // Variants
    variants: ProductVariant[] = [];
    variantsLoading = false;
    variantsError: string | null = null;

    // Bill of Materials
    billOfMaterials: BillOfMaterials[] = [];
    bomItems: BOMItem[] = [];
    bomLoading = false;
    bomError: string | null = null;

    // Pricing
    pricing: ProductPricing[] = [];
    pricingLoading = false;
    pricingError: string | null = null;

    // Inventory
    inventory: ProductInventory[] = [];
    inventoryLoading = false;
    inventoryError: string | null = null;

    // Constructor
    constructor(rootStore: RootStore) {
        makeAutoObservable(this, {
            rootStore: false,
        });
        this.rootStore = rootStore;
    }

    // Service store access
    get productsStore(): ServiceStore {
        return this.rootStore.productsStore as ServiceStore;
    }

    get productCategoriesStore(): ServiceStore {
        return this.rootStore.productCategoriesStore as ServiceStore;
    }

    get productVariantsStore(): ServiceStore {
        return this.rootStore.productVariantsStore as ServiceStore;
    }

    get billOfMaterialsStore(): ServiceStore {
        return this.rootStore.billOfMaterialsStore as ServiceStore;
    }

    get bomItemsStore(): ServiceStore {
        return this.rootStore.bomItemsStore as ServiceStore;
    }

    get productPricingStore(): ServiceStore {
        return this.rootStore.productPricingStore as ServiceStore;
    }

    get productInventoryStore(): ServiceStore {
        return this.rootStore.productInventoryStore as ServiceStore;
    }

    // Actions
    setLoading(loading: boolean) {
        this.isLoading = loading;
    }

    setError(error: string | null) {
        this.error = error;
    }

    // Async actions - Categories
    async loadCategories() {
        try {
            this.categoriesLoading = true;
            this.categoriesError = null;

            // Mock data fallback for development
            if (!this.rootStore.app || !this.rootStore.app.service) {
                runInAction(() => {
                    this.categories = [
                        { id: 1, categoryCode: 'CULVERT', name: 'Culverts', description: 'Drainage culverts', active: true },
                        { id: 2, categoryCode: 'KRAIL', name: 'K-Rail', description: 'Traffic barriers', active: true },
                        { id: 3, categoryCode: 'PRECAST', name: 'Precast Elements', description: 'General precast products', active: true },
                    ];
                    this.categoryTree = this.categories;
                    this.categoriesLoading = false;
                });
                return;
            }

            const result = await this.productCategoriesStore.find({
                query: { active: true, $sort: { sortOrder: 1, name: 1 } }
            });

            runInAction(() => {
                this.categories = (result?.data || []) as unknown as ProductCategory[];
                this.categoryTree = this.buildCategoryTree(this.categories);
                this.categoriesError = null;
            });
        } catch (error) {
            runInAction(() => {
                this.categoriesError = error.message || 'Failed to load categories';
                console.error('Failed to load categories:', error);
            });
        } finally {
            runInAction(() => {
                this.categoriesLoading = false;
            });
        }
    }

    // Async actions - Products
    async loadProducts(query?: any) {
        try {
            this.productsLoading = true;
            this.productsError = null;

            // Mock data fallback for development
            if (!this.rootStore.app || !this.rootStore.app.service) {
                runInAction(() => {
                    this.products = [
                        {
                            id: 1,
                            productCode: 'CULV-24-STD',
                            name: '24" Standard Culvert',
                            description: 'Standard reinforced concrete culvert, 24 inch diameter',
                            productType: 'culvert',
                            categoryId: 1,
                            basePrice: 150.00,
                            weight: 2800,
                            dimensions: '24" x 12"',
                            material: 'Reinforced Concrete',
                            specifications: '{"strength": "4000psi", "finish": "smooth"}',
                            active: true,
                        },
                        {
                            id: 2,
                            productCode: 'KRAIL-10-STD',
                            name: '10ft K-Rail Standard',
                            description: 'Standard K-Rail traffic barrier, 10 feet long',
                            productType: 'k-rail',
                            categoryId: 2,
                            basePrice: 85.00,
                            weight: 4200,
                            dimensions: '10ft x 32" x 24"',
                            material: 'Reinforced Concrete',
                            specifications: '{"strength": "4000psi", "finish": "smooth"}',
                            active: true,
                        },
                        {
                            id: 3,
                            productCode: 'PREC-CUSTOM-001',
                            name: 'Custom Precast Element',
                            description: 'Custom architectural precast element',
                            productType: 'precast',
                            categoryId: 3,
                            basePrice: 250.00,
                            weight: 1800,
                            dimensions: 'Variable',
                            material: 'Architectural Concrete',
                            specifications: '{"strength": "5000psi", "finish": "architectural"}',
                            active: true,
                        },
                    ];
                    this.productsLoading = false;
                });
                return;
            }

            const defaultQuery = {
                active: true,
                $sort: { name: 1 },
                $limit: 50,
                ...query
            };

            const result = await this.productsStore.find({ query: defaultQuery });

            runInAction(() => {
                this.products = (result?.data || []) as unknown as Product[];
                this.productsError = null;
            });
        } catch (error) {
            runInAction(() => {
                this.productsError = error.message || 'Failed to load products';
                console.error('Failed to load products:', error);
            });
        } finally {
            runInAction(() => {
                this.productsLoading = false;
            });
        }
    }

    async getProduct(productId: number) {
        try {
            this.setLoading(true);

            const product = await this.productsStore.get(productId);

            runInAction(() => {
                this.selectedProduct = product as unknown as Product;
                this.error = null;
            });

            return product;
        } catch (error) {
            runInAction(() => {
                this.error = error.message || 'Failed to load product';
                console.error('Failed to load product:', error);
            });
            throw error;
        } finally {
            this.setLoading(false);
        }
    }

    async createProduct(data: ProductData) {
        try {
            this.setLoading(true);

            const newProduct = await this.productsStore.create(data);

            runInAction(() => {
                this.products = [...this.products, newProduct as unknown as Product];
                this.error = null;
            });

            return newProduct;
        } catch (error) {
            runInAction(() => {
                this.error = error.message || 'Failed to create product';
                console.error('Failed to create product:', error);
            });
            throw error;
        } finally {
            this.setLoading(false);
        }
    }

    async updateProduct(productId: number, data: Partial<ProductData>) {
        try {
            this.setLoading(true);

            const updatedProduct = await this.productsStore.patch(productId, data);

            runInAction(() => {
                const index = this.products.findIndex(p => p.id === productId);
                if (index !== -1) {
                    this.products[index] = { ...this.products[index], ...(updatedProduct as unknown as Product) };
                }
                if (this.selectedProduct?.id === productId) {
                    this.selectedProduct = { ...this.selectedProduct, ...(updatedProduct as unknown as Product) };
                }
                this.error = null;
            });

            return updatedProduct;
        } catch (error) {
            runInAction(() => {
                this.error = error.message || 'Failed to update product';
                console.error('Failed to update product:', error);
            });
            throw error;
        } finally {
            this.setLoading(false);
        }
    }

    // Async actions - Variants
    async loadVariants(productId: number) {
        try {
            this.variantsLoading = true;
            this.variantsError = null;

            const result = await this.productVariantsStore.find({
                query: { productId, active: true, $sort: { isDefault: -1, name: 1 } }
            });

            runInAction(() => {
                this.variants = (result?.data || []) as unknown as ProductVariant[];
                this.variantsError = null;
            });
        } catch (error) {
            runInAction(() => {
                this.variantsError = error.message || 'Failed to load variants';
                console.error('Failed to load variants:', error);
            });
        } finally {
            runInAction(() => {
                this.variantsLoading = false;
            });
        }
    }

    async createVariant(data: ProductVariantData) {
        try {
            this.setLoading(true);

            const newVariant = await this.productVariantsStore.create(data);

            runInAction(() => {
                this.variants = [...this.variants, newVariant as unknown as ProductVariant];
                this.error = null;
            });

            return newVariant;
        } catch (error) {
            runInAction(() => {
                this.error = error.message || 'Failed to create variant';
                console.error('Failed to create variant:', error);
            });
            throw error;
        } finally {
            this.setLoading(false);
        }
    }

    // Async actions - Bill of Materials
    async loadBOMs(productId: number) {
        try {
            this.bomLoading = true;
            this.bomError = null;

            const result = await this.billOfMaterialsStore.find({
                query: { productId, active: true, $sort: { version: -1, createdAt: -1 } }
            });

            runInAction(() => {
                this.billOfMaterials = (result?.data || []) as unknown as BillOfMaterials[];
                this.bomError = null;
            });
        } catch (error) {
            runInAction(() => {
                this.bomError = error.message || 'Failed to load BOMs';
                console.error('Failed to load BOMs:', error);
            });
        } finally {
            runInAction(() => {
                this.bomLoading = false;
            });
        }
    }

    async loadBOMItems(bomId: number) {
        try {
            this.bomLoading = true;
            this.bomError = null;

            const result = await this.bomItemsStore.find({
                query: { bomId, $sort: { sortOrder: 1, name: 1 } }
            });

            runInAction(() => {
                this.bomItems = (result?.data || []) as unknown as BOMItem[];
                this.bomError = null;
            });
        } catch (error) {
            runInAction(() => {
                this.bomError = error.message || 'Failed to load BOM items';
                console.error('Failed to load BOM items:', error);
            });
        } finally {
            runInAction(() => {
                this.bomLoading = false;
            });
        }
    }

    async createBOM(data: BillOfMaterialsData) {
        try {
            this.setLoading(true);

            const newBOM = await this.billOfMaterialsStore.create(data);

            runInAction(() => {
                this.billOfMaterials = [...this.billOfMaterials, newBOM as unknown as BillOfMaterials];
                this.error = null;
            });

            return newBOM;
        } catch (error) {
            runInAction(() => {
                this.error = error.message || 'Failed to create BOM';
                console.error('Failed to create BOM:', error);
            });
            throw error;
        } finally {
            this.setLoading(false);
        }
    }

    async createBOMItem(data: BOMItemData) {
        try {
            this.setLoading(true);

            const newItem = await this.bomItemsStore.create(data);

            runInAction(() => {
                this.bomItems = [...this.bomItems, newItem as unknown as BOMItem];
                this.error = null;
            });

            return newItem;
        } catch (error) {
            runInAction(() => {
                this.error = error.message || 'Failed to create BOM item';
                console.error('Failed to create BOM item:', error);
            });
            throw error;
        } finally {
            this.setLoading(false);
        }
    }

    // Async actions - Pricing
    async loadPricing(productId: number, variantId?: number) {
        try {
            this.pricingLoading = true;
            this.pricingError = null;

            const query: any = { productId, active: true, $sort: { effectiveDate: -1 } };
            if (variantId) {
                query.variantId = variantId;
            }

            const result = await this.productPricingStore.find({ query });

            runInAction(() => {
                this.pricing = (result?.data || []) as unknown as ProductPricing[];
                this.pricingError = null;
            });
        } catch (error) {
            runInAction(() => {
                this.pricingError = error.message || 'Failed to load pricing';
                console.error('Failed to load pricing:', error);
            });
        } finally {
            runInAction(() => {
                this.pricingLoading = false;
            });
        }
    }

    async createPricingRule(data: ProductPricingData) {
        try {
            this.setLoading(true);

            const newPricing = await this.productPricingStore.create(data);

            runInAction(() => {
                this.pricing = [...this.pricing, newPricing as unknown as ProductPricing];
                this.error = null;
            });

            return newPricing;
        } catch (error) {
            runInAction(() => {
                this.error = error.message || 'Failed to create pricing rule';
                console.error('Failed to create pricing rule:', error);
            });
            throw error;
        } finally {
            this.setLoading(false);
        }
    }

    // Async actions - Inventory
    async loadInventory(productId: number, variantId?: number) {
        try {
            this.inventoryLoading = true;
            this.inventoryError = null;

            const query: any = { productId, active: true, $sort: { location: 1 } };
            if (variantId) {
                query.variantId = variantId;
            }

            const result = await this.productInventoryStore.find({ query });

            runInAction(() => {
                this.inventory = (result?.data || []) as unknown as ProductInventory[];
                this.inventoryError = null;
            });
        } catch (error) {
            runInAction(() => {
                this.inventoryError = error.message || 'Failed to load inventory';
                console.error('Failed to load inventory:', error);
            });
        } finally {
            runInAction(() => {
                this.inventoryLoading = false;
            });
        }
    }

    async createInventoryRecord(data: ProductInventoryData) {
        try {
            this.setLoading(true);

            const newInventory = await this.productInventoryStore.create(data);

            runInAction(() => {
                this.inventory = [...this.inventory, newInventory as unknown as ProductInventory];
                this.error = null;
            });

            return newInventory;
        } catch (error) {
            runInAction(() => {
                this.error = error.message || 'Failed to create inventory record';
                console.error('Failed to create inventory record:', error);
            });
            throw error;
        } finally {
            this.setLoading(false);
        }
    }

    async recordInventoryMovement(inventoryId: number, movementType: string, quantity: number, notes?: string) {
        try {
            this.setLoading(true);

            // This would typically be a custom API endpoint or service method
            // For now, update the inventory record directly
            const updateData = {
                lastMovementType: movementType,
                lastMovementDate: new Date().toISOString(),
                notes: notes || '',
                // Adjust quantity based on movement type
                ...(movementType === 'issue' && { quantityOnHand: quantity * -1 }),
                ...(movementType === 'receipt' && { quantityOnHand: quantity }),
                ...(movementType === 'adjustment' && { quantityOnHand: quantity }),
            };

            const updatedInventory = await this.productInventoryStore.patch(inventoryId, updateData);

            runInAction(() => {
                const index = this.inventory.findIndex(inv => inv.id === inventoryId);
                if (index !== -1) {
                    this.inventory[index] = { ...this.inventory[index], ...(updatedInventory as unknown as ProductInventory) };
                }
                this.error = null;
            });

            return updatedInventory;
        } catch (error) {
            runInAction(() => {
                this.error = error.message || 'Failed to record inventory movement';
                console.error('Failed to record inventory movement:', error);
            });
            throw error;
        } finally {
            this.setLoading(false);
        }
    }

    // Computed values
    get hasData() {
        return this.products.length > 0;
    }

    get activeProducts() {
        return this.products.filter(p => p.active);
    }

    get categoriesMap() {
        return this.categories.reduce((map, cat) => {
            map[cat.id] = cat;
            return map;
        }, {} as Record<number, ProductCategory>);
    }

    // Helper methods
    private buildCategoryTree(categories: ProductCategory[]): ProductCategory[] {
        const map = new Map<number, ProductCategory>();
        const roots: ProductCategory[] = [];

        // First pass: create map
        categories.forEach(cat => {
            map.set(cat.id, { ...cat, children: [] });
        });

        // Second pass: build tree
        categories.forEach(cat => {
            const node = map.get(cat.id)!;
            if (cat.parentCategoryId && map.has(cat.parentCategoryId)) {
                const parent = map.get(cat.parentCategoryId)!;
                if (!parent.children) parent.children = [];
                parent.children.push(node);
            } else {
                roots.push(node);
            }
        });

        return roots;
    }
}

export default ProductStore;
